import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  ConditionalRenderer,
  CountriesDropdown,
  SelectInputV1,
  useLanguageTranslator,
} from "../../../../../components";
import { useCallback, useMemo } from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import YearPickerField from "./yearPicker";
import dayjs from "dayjs";
import { useApolloQuery } from "../../../../../hooks";

const GET_INDICATORS = gql`
  query GetIndicators {
    indicators {
      id
      label
    }
  }
`;

const GET_SINGLE_INDICATOR_VALUE = gql`
  query GetIndicatorValue($id: ID!) {
    indicatorValue(id: $id) {
      countryId
      createdAt
      description
      id
      indicator {
        createdAt
        id
        indicatorCategoryId
        label
        peopleSource
        policySource
        practiceSource
        question
        ratingType
        themeId
        updatedAt
      }
      indicatorId
      source
      updatedAt
      value
      year
    }
  }
`;

// mutation {
//   indicatorValueCreate(
//     input: {
//       indicatorValueInput: {
//         indicatorId: 1
//         countryId: 1
//         year: 1
//         value: "string"
//         source:"string"
//         description:"string"
//       }
//     }
//   ) {
//     clientMutationId
//     indicatorValue {
//       countryId
//       createdAt
//       description
//       id
//       indicator{
//         id
//         label
//       }
//       indicatorId
//       updatedAt
//        source
//       value
//       year
//     }
//   }
// }

const CREATE_INDICATOR_VALUE = gql`
  mutation CreateIndicatorValue(
    $indicatorId: Int!
    $countryId: Int!
    $year: Int!
    $value: String!
    $source: String!
    $description: String!
  ) {
    indicatorValueCreate(
      input: {
        indicatorValueInput: {
          indicatorId: $indicatorId
          countryId: $countryId
          year: $year
          value: $value
          source: $source
          description: $description
        }
      }
    ) {
      clientMutationId
      indicatorValue {
        id
      }
    }
  }
`;

const UPDATE_INDICATOR_VALUE = gql`
  mutation UpdateIndicatorValue(
    $id: ID!
    $indicatorId: Int!
    $countryId: Int!
    $year: Int!
    $value: String!
    $source: String!
    $description: String!
  ) {
    indicatorValueUpdate(
      input: {
        id: $id
        indicatorValueInput: {
          indicatorId: $indicatorId
          countryId: $countryId
          year: $year
          value: $value
          source: $source
          description: $description
        }
      }
    ) {
      clientMutationId
      indicatorValue {
        id
      }
    }
  }
`;

function IndicatorValuesAction({ isUpdate = false }: any) {
  const { translator } = useLanguageTranslator();
  const { user } = useAuthSession() as any;
  const notify = useNotify();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const indicator_value_id = searchParams.get("id");

  const { data: indicatorsList } = useApolloQuery({
    queryDocument: GET_INDICATORS,
    isPrivateCall: true,
  });

  const { data: indicatorValueData } = useQuery(GET_SINGLE_INDICATOR_VALUE, {
    variables: { id: indicator_value_id },
    skip: !indicator_value_id,
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [createIndicatorValues, { loading, error }] = useMutation(
    CREATE_INDICATOR_VALUE,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const [updateIndicatorsValues] = useMutation(UPDATE_INDICATOR_VALUE, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!indicatorValueData?.indicatorValue) return undefined;

    const { countryId, value, source, description, indicator, year } =
      indicatorValueData?.indicatorValue;

    return {
      indicator_label: indicator?.id,
      country_name: countryId,
      value: value,
      source: source,
      description: description,
      year: dayjs().year(year).startOf("year"),
    };
  }, [indicatorValueData]);

  const form = useForm<any>({
    defaultValues: {
      indicator_label: "",
      country_name: null,
      value: null,
      source: null,
      description: null,
      year: "",
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    const yearOnly = data?.year?.$y;
    if (actionType === "update") {
      try {
        const response = await updateIndicatorsValues({
          variables: {
            id: Number(indicator_value_id),
            indicatorId: Number(data?.indicator_label),
            countryId: Number(data?.country_name),
            year: yearOnly,
            value: data?.value,
            source: data?.source,
            description: data?.description,
          },
        });
        if (response.data) {
          notify.success(`Indicator Value Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/indicators-values");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }

    // add below
    const response = await createIndicatorValues({
      variables: {
        indicatorId: Number(data?.indicator_label),
        countryId: Number(data?.country_name),
        year: yearOnly,
        value: data?.value,
        source: data?.source,
        description: data?.description,
      },
    });
    try {
      if (actionType === "save") {
        if (response.data) {
          notify.success(`Indicator Value Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/indicators-values");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`Indicator Value Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {}
  }, []);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
          pt={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="indicator_label"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("Indicator Label")}</FormLabel>
                  <SelectInputV1
                    field={field}
                    data={indicatorsList?.indicators}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <YearPickerField />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="country_name"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("Country Name")}</FormLabel>
                  <CountriesDropdown {...field} />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="value"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("Value")}</FormLabel>
                  <OutlinedInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="source"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("Source")}</FormLabel>
                  <OutlinedInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="description"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>
                    {translator("dashboardTableHead.Description")}
                  </FormLabel>
                  <OutlinedInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/indicators-values"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </FormProvider>
  );
}

export { IndicatorValuesAction };
